<template>
  <div class="isolate h-64 w-full overflow-hidden rounded-3xl">
    <div ref="mapContainer" class="h-full w-full"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, watch } from "vue";

type LeafletMapLike = {
  setView: (coords: [number, number], zoom: number) => LeafletMapLike;
  invalidateSize: () => void;
  remove: () => void;
};

type LeafletMarkerLike = {
  setLatLng: (coords: [number, number]) => void;
  addTo: (map: LeafletMapLike) => LeafletMarkerLike;
};

type LeafletModuleLike = {
  map: (element: HTMLElement) => LeafletMapLike;
  maplibreGL: (options: Record<string, unknown>) => {
    addTo: (map: LeafletMapLike) => unknown;
  };
  tileLayer: (
    url: string,
    options: Record<string, unknown>,
  ) => { addTo: (map: LeafletMapLike) => unknown };
  divIcon: (options: Record<string, unknown>) => unknown;
  marker: (
    coords: [number, number],
    options: Record<string, unknown>,
  ) => LeafletMarkerLike;
};

interface Props {
  lat: number;
  lon: number;
  zoom?: number;
}

const props = withDefaults(defineProps<Props>(), {
  zoom: 15,
});

const mapContainer = ref<HTMLElement | null>(null);
let L: LeafletModuleLike | null = null;
let map: LeafletMapLike | null = null;
let marker: LeafletMarkerLike | null = null;

function loadLeaflet(): Promise<void> {
  return new Promise((res) => {
    const leafletWindow = window as Window & { L?: LeafletModuleLike };
    if (leafletWindow.L?.maplibreGL) {
      L = leafletWindow.L;
      return res();
    }
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(css);

    const s = document.createElement("script");
    s.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    s.onload = () => {
      const mapLibreCss = document.createElement("link");
      mapLibreCss.rel = "stylesheet";
      mapLibreCss.href = "https://unpkg.com/maplibre-gl@5/dist/maplibre-gl.css";
      document.head.appendChild(mapLibreCss);

      const mapLibreScript = document.createElement("script");
      mapLibreScript.src =
        "https://unpkg.com/maplibre-gl@5/dist/maplibre-gl.js";
      mapLibreScript.onload = () => {
        const bridgeScript = document.createElement("script");
        bridgeScript.src =
          "https://unpkg.com/@maplibre/maplibre-gl-leaflet/leaflet-maplibre-gl.js";
        bridgeScript.onload = () => {
          L = (window as Window & { L?: LeafletModuleLike }).L ?? null;
          res();
        };
        document.body.appendChild(bridgeScript);
      };
      document.body.appendChild(mapLibreScript);
    };
    document.body.appendChild(s);
  });
}

async function initMap() {
  await loadLeaflet();
  if (!mapContainer.value) return;

  const leaflet = L;
  if (!leaflet) return;

  const mapInstance = leaflet
    .map(mapContainer.value)
    .setView([props.lat, props.lon], props.zoom);
  map = mapInstance;

  // Use OpenFreeMap Positron (closest visual match to Carto light basemap)
  leaflet
    .maplibreGL({
      style: "https://tiles.openfreemap.org/styles/positron",
    })
    .addTo(mapInstance);

  // Add a pink marker
  const pinHtml = `
        <svg width="28" height="36" viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 0C7 0 3.5 3.5 3.5 8.5c0 6.5 8.5 17.5 8.5 17.5s8.5-11 8.5-17.5C20.5 3.5 17 0 12 0z" fill="#ec4899"/>
            <circle cx="12" cy="8.5" r="3.5" fill="white" opacity="0.9"/>
        </svg>`;

  const pinkIcon = leaflet.divIcon({
    className: "custom-pin",
    html: pinHtml,
    iconAnchor: [12, 28],
  });
  marker = leaflet
    .marker([props.lat, props.lon], { icon: pinkIcon })
    .addTo(mapInstance);

  // Ensure correct sizing
  setTimeout(() => mapInstance.invalidateSize(), 50);
}

function updateMapPosition() {
  const mapInstance = map;
  const markerInstance = marker;
  if (mapInstance && markerInstance) {
    mapInstance.setView([props.lat, props.lon], props.zoom);
    markerInstance.setLatLng([props.lat, props.lon]);
  }
}

// Watch for prop changes and update map
watch(
  () => [props.lat, props.lon, props.zoom],
  () => {
    updateMapPosition();
  },
);

onMounted(async () => {
  await nextTick();
  await initMap();
});
</script>

<style scoped>
/* Make tiles grayscale */
:deep(.leaflet-tile) {
  filter: grayscale(100%) contrast(0.95) brightness(0.95) !important;
}

:deep(.maplibregl-canvas) {
  filter: grayscale(100%) contrast(0.95) brightness(0.95) !important;
}

/* Custom pin styles */
:deep(.custom-pin) {
  display: inline-block;
  line-height: 0;
}

:deep(.custom-pin svg) {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.18));
}

/* Ensure map container fills area */
.leaflet-container {
  height: 100%;
  width: 100%;
}
</style>
